// Reading api2.govglance.org's /elections dates, ballot and offices surface.
//
// A sibling to localCandidatesApi rather than part of it: that module answers
// "who is running", this one answers "when is the vote, what is on it, and what
// counties does this state have". They share the row reader -- normalizeCandidate
// is imported, not copied -- so a candidate reads the same whether they arrived
// from a ballot or from a county list.
//
// No auth. api2 is read-only and public -- do NOT send the api.govglance.org
// bearer token here.
//
// The endpoints sit at different granularities, and the split is not arbitrary:
//
//   /elections/dates    statewide only. There is no district parameter, and the
//                       API says why: a date is set statewide, so two
//                       neighbouring districts vote on the same day. Districts
//                       cannot narrow this call and asking them to would be
//                       inventing a filter the data does not support.
//   /elections/ballot   the contests on one of those dates, plus the candidates
//                       who filed for the offices each contest covers. Requires
//                       an exact ISO date, so it always runs second.
//   /elections/offices  the election agencies themselves. Used here for the one
//                       thing the candidate rows cannot supply: a stable list
//                       of a state's counties. See fetchStateCounties.
//
// Fields are read through `pick` because these endpoints declare no response
// schema (every one is `additionalProperties: true` in the OpenAPI document).
// Names marked GUESS have not been confirmed against a live response; if
// something renders empty, those are the lines to check first.

import {
  API2_BASE,
  normalizeCandidate,
  pick,
} from './localCandidatesApi';

// What api2 calls the kinds of election a single date can carry. A date can
// hold more than one -- a federal primary beside a state primary, a runoff for
// one office beside a primary for another -- which is why /elections/ballot
// returns every matching row together and offers this as the narrowing param.
export const ELECTION_TYPES = ['primary', 'general', 'runoff', 'special'];

// Both endpoints cap well above anything a state produces on one day.
export const DEFAULT_DATES_LIMIT = 50;
export const DEFAULT_BALLOT_LIMIT = 500;

// Texas has 254 counties, so a state's whole roster fits in one page.
export const DEFAULT_OFFICES_LIMIT = 1000;

// ─── Dates ────────────────────────────────────────────────────────────────────

// 'YYYY-MM-DD' is parsed as UTC midnight by the Date constructor, which renders
// as the previous day everywhere west of Greenwich -- an election on the 3rd
// shown as the 2nd across the entire United States. Constructed from the parts
// instead, so the date means the same calendar day it says.
export const parseElectionDate = (value) => {
  if (!value) return null;

  const iso = String(value).trim();
  const parts = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (parts) {
    const [, year, month, day] = parts;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Election day itself is not past. Somebody opening this at 9am on the third
// wants to be told the election is today, not that they missed it.
const isPastDate = (date) => Boolean(date) && date < startOfToday();

const daysFromToday = (date) => {
  if (!date) return null;
  const ms = date.getTime() - startOfToday().getTime();
  return Math.round(ms / 86400000);
};

export const formatElectionDate = (date, locale) => {
  if (!date) return '';
  try {
    return date.toLocaleDateString(locale || undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    // An unsupported locale tag should not take the row down with it.
    return date.toDateString();
  }
};

const toIsoDay = (date) => {
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const normalizeElection = (raw, index = 0) => {
  if (!raw || typeof raw !== 'object') return null;

  const rawDate = pick(raw, 'election_date', 'date'); // GUESS on the second
  const date = parseElectionDate(rawDate);
  // A row with no readable date cannot be placed on a calendar, so it is
  // dropped rather than rendered as an election of unknown date.
  if (!date) return null;

  const dateISO = toIsoDay(date);

  // The row's own words wherever it has them. "Primary Election" from the state
  // beats a label composed here from a type code, and a special election's name
  // is the one thing that says which seat it fills.
  const type = String(pick(raw, 'election_type', 'type') || '').toLowerCase() || null;
  const name = pick(raw, 'election_name', 'name', 'title'); // GUESS

  // Which offices the contest covers. /elections/ballot matches candidates on
  // this, so it is worth carrying even though nothing renders it directly yet.
  const offices = Array.isArray(raw.offices)
    ? raw.offices.filter(Boolean).map(String)
    : [];

  const election = {
    date,
    dateISO,
    type,
    name: name ? String(name).trim() : null,

    stateAbbr: String(pick(raw, 'state_abbr', 'state') || '').toUpperCase() || '',
    level: pick(raw, 'level') || null,
    year: pick(raw, 'election_year', 'year') || String(date.getFullYear()),

    offices,
    source: pick(raw, 'source', 'source_url', 'official_source_url'),

    isPast: isPastDate(date),
    daysUntil: daysFromToday(date),

    order: index,
  };

  // A date can carry several contests, so the date alone is not unique.
  election.key = [dateISO, type || 'election', index].join(':');

  return election;
};

// ─── Districts ────────────────────────────────────────────────────────────────

// The saved location's district fields, in the shape an annotator wants. Kept
// here rather than inline at each call site so the three field names are
// written once.
export const districtsFromLocation = (location) => ({
  congressional: location?.congressionalDistrict || null,
  upper: location?.stateUpperDistrict || null,
  lower: location?.stateLowerDistrict || null,
});

// District labels are not numbers and are not spelled consistently: Idaho has
// 27 and 27A, New Hampshire has "Belknap 1", and the same seat arrives as "3",
// "03" and "District 3" depending on which state published the row. Reduce both
// sides to one spelling before comparing.
export const bareDistrict = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/\b(DISTRICT|DIST\.?|NO\.?)\b/g, ' ')
    .replace(/[^0-9A-Z]/g, '')
    .replace(/^0+(?=\d)/, '');

export const sameDistrict = (a, b) => {
  const left = bareDistrict(a);
  const right = bareDistrict(b);
  return Boolean(left) && left === right;
};

/**
 * Which saved district, if any, a candidate's seat is drawn from.
 *
 * Returns 'congressional' | 'upper' | 'lower' | null.
 *
 * Deliberately conservative, and null is a normal answer rather than a failure.
 * /elections/candidates has no chamber parameter, so district 16 means both the
 * senate's 16th and the house's 16th -- the exact ambiguity /districts/officials
 * refuses to guess at -- and the office string is the only thing here that can
 * tell them apart. Where it cannot, the row is left unmarked.
 *
 * Statewide seats -- U.S. Senate, governor, attorney general -- return null
 * because they are not drawn around a district at all.
 */
export const districtSlotFor = (candidate) => {
  const office = String(candidate?.office || '');
  const level = String(candidate?.level || '').toLowerCase();

  // Names both chambers at once, so it identifies neither. Tested first, or the
  // ASSEMBLY test below would claim it for the lower chamber.
  if (/\bgeneral assembly\b/i.test(office)) return null;

  const isSenate = /\bsenat/i.test(office);
  const isHouse = /\b(house|representative|assembly|delegate)/i.test(office);

  if (level === 'federal') {
    // A U.S. Senator represents the whole state.
    if (isSenate) return null;
    return isHouse || /\bcongress/i.test(office) ? 'congressional' : null;
  }

  if (level === 'state') {
    // Nebraska's unicameral body is "Legislature", and /districts returns it as
    // the upper chamber, so the saved district is in stateUpperDistrict.
    if (isSenate || /\blegislature\b/i.test(office)) return 'upper';
    if (isHouse) return 'lower';
    return null;
  }

  // County and city seats have wards and precincts of their own, and nothing in
  // the saved location describes them.
  return null;
};

/**
 * Mark the rows drawn from one of the reader's own districts.
 *
 * Adds two fields and removes nothing: `districtSlot` (which saved district the
 * seat would be matched against, or null) and `inDistrict`. A row with a slot
 * but no match is a real contest in a neighbouring district, which is worth
 * seeing -- a caller can group on `inDistrict` without ever dropping a row.
 */
export const annotateDistrictMatches = (candidates, districts) => {
  const saved = districts || {};

  return (candidates || []).map((candidate) => {
    const districtSlot = districtSlotFor(candidate);
    const wanted = districtSlot ? saved[districtSlot] : null;

    return {
      ...candidate,
      districtSlot,
      inDistrict: Boolean(wanted && sameDistrict(candidate.district, wanted)),
    };
  });
};

// ─── Requests ─────────────────────────────────────────────────────────────────

// One page of results per distinct query, for as long as the tab is open. Same
// reasoning as the candidate cache: the dates for a state change a few times a
// year and its counties essentially never, so refetching them every time
// somebody switches topics buys a flash of empty screen with a round trip.
const cache = new Map();

export const clearElectionCache = () => cache.clear();

const readRows = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }

  // An empty result arrives as a matching key holding an empty array, so
  // reaching here means none of the expected keys exists at all -- the response
  // shape changed, or these names were wrong to begin with. Said out loud
  // rather than returning an empty list that reads as "no data".
  console.warn('[elections] no rows found on response', {
    tried: keys,
    payloadKeys: Object.keys(payload),
    arrayKeys: Object.keys(payload).filter((key) => Array.isArray(payload[key])),
  });
  return [];
};

// api2 says why in the body -- a rejected parameter names itself, and an
// unknown value names the valid ones. Throwing only the status discards the one
// part of the answer that says what to change.
const requestJson = async (path, params, { signal } = {}) => {
  const query = params.toString();
  const url = `${API2_BASE}${path}?${query}`;

  const response = await fetch(url, { signal });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.text();
      detail = body ? ` - ${body.slice(0, 300)}` : '';
    } catch {
      // The status alone is still worth reporting.
    }

    const error = new Error(`api2 ${path} responded ${response.status}${detail}`);
    error.status = response.status;
    error.query = query;
    throw error;
  }

  return response.json();
};

/**
 * Scheduled elections for a state, soonest first.
 *
 * Past elections are excluded by the endpoint unless asked for. Federal rows
 * come from the FEC and the November general is derived from statute, so every
 * state has a next election even in a year with no federal contest on its
 * ballot -- an empty list here is unusual enough to be worth surfacing the
 * state's own election office rather than reporting "no elections".
 *
 * Municipal dates are not in this data at all: there is no national source for
 * them. Whatever the response says about that arrives in `notes`, which is
 * passed through verbatim rather than summarized.
 */
export const fetchElectionDates = async (
  stateAbbr,
  { includePast = false, limit = DEFAULT_DATES_LIMIT, signal } = {}
) => {
  if (!stateAbbr) throw new Error('state is required');

  const params = new URLSearchParams({
    state: String(stateAbbr).toUpperCase(),
    include_past: String(Boolean(includePast)),
    limit: String(limit),
  });

  const cacheKey = `dates?${params}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const payload = await requestJson('/elections/dates', params, { signal });

  const elections = readRows(payload, 'elections', 'results', 'dates')
    .map(normalizeElection)
    .filter(Boolean)
    .sort((a, b) => a.date - b.date);

  const result = {
    elections,
    // The first one that has not happened. With include_past off this is just
    // the first row, but it stays correct when a caller asks for history.
    next: elections.find((election) => !election.isPast) || null,
    notes: payload?.notes || null,
    coverage: payload?.coverage || null,
  };

  // Only a successful answer is kept. A failure is not a fact about the data,
  // and caching one would turn a moment of bad network into a page that stays
  // broken until the tab is closed.
  cache.set(cacheKey, result);

  return result;
};

/**
 * One election date, the contests on it, and who filed for them.
 *
 * `date` must be an exact ISO day, which is why this always runs after
 * fetchElectionDates rather than beside it.
 *
 * A date can carry more than one contest, so `contests` is a list even when the
 * caller asked about "the election". Pass `electionType` to narrow it.
 */
export const fetchElectionBallot = async (
  { stateAbbr, date, electionType, limit = DEFAULT_BALLOT_LIMIT },
  { signal } = {}
) => {
  if (!stateAbbr) throw new Error('state is required');
  if (!date) throw new Error('date is required');

  if (electionType && !ELECTION_TYPES.includes(electionType)) {
    throw new Error(`unknown election type ${electionType}`);
  }

  const params = new URLSearchParams({
    state: String(stateAbbr).toUpperCase(),
    date: String(date),
    limit: String(limit),
  });
  if (electionType) params.set('election_type', electionType);

  const cacheKey = `ballot?${params}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const payload = await requestJson('/elections/ballot', params, { signal });

  const candidateRows = readRows(payload, 'candidates', 'results');

  const result = {
    contests: readRows(payload, 'elections', 'contests')
      .map(normalizeElection)
      .filter(Boolean),

    // Read with the same function the county lists use, so a candidate does not
    // read differently depending on which screen opened them.
    candidates: candidateRows.map(normalizeCandidate).filter(Boolean),

    truncated: candidateRows.length >= limit,
    notes: payload?.notes || null,
    coverage: payload?.coverage || null,
  };

  cache.set(cacheKey, result);

  return result;
};

/**
 * The counties of one state, for a county picker.
 *
 * Read from the election agencies rather than from the candidate rows, and that
 * is the whole point of it. Deriving the list from candidates makes the picker
 * a reflection of whatever happened to load: the moment a county filter is
 * applied, the rows collapse to that one county, so the only option on offer is
 * the county already selected and the filter can never be changed. The agency
 * roster does not move when the candidate query does.
 *
 * Never throws. A county picker is a convenience, and a caller that cannot get
 * one should fall back to whatever the rows reported rather than lose the list
 * entirely -- so a failure here returns an empty array and says so once.
 */
export const fetchStateCounties = async (stateAbbr, { signal } = {}) => {
  if (!stateAbbr) return [];

  const params = new URLSearchParams({
    state: String(stateAbbr).toUpperCase(),
    level: 'county',
    limit: String(DEFAULT_OFFICES_LIMIT),
  });

  const cacheKey = `counties?${params}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  try {
    const payload = await requestJson('/elections/offices', params, { signal });
    const rows = readRows(payload, 'offices', 'results', 'agencies');

    // One agency per county, but a county with both a board and a clerk would
    // otherwise appear twice, so the names are deduplicated.
    const names = [
      ...new Set(
        rows
          .map((row) => pick(row, 'county', 'county_name', 'jurisdiction_name')) // GUESS
          .filter(Boolean)
          .map((name) => String(name).trim())
      ),
    ].sort((a, b) => a.localeCompare(b));

    if (!names.length && rows.length) {
      // Rows came back but no name could be read off them, which means the
      // field is called something this does not try. Worth saying out loud
      // rather than silently degrading to the candidate-derived list.
      console.warn('[elections] county names not found on /elections/offices rows', {
        keys: Object.keys(rows[0] || {}),
      });
    }

    cache.set(cacheKey, names);
    return names;
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    console.warn('[elections] county list unavailable', err.message);
    return [];
  }
};